import {
    AppBar,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Tooltip,
    useTheme as useMuiTheme
} from "@mui/material";
import {observer} from 'mobx-react';
import * as React from "react";
import {useEffect, useState} from "react";
import Typography from "@mui/material/Typography";
import BuildIcon from '@mui/icons-material/Build';
import MenuIcon from '@mui/icons-material/Menu';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import EngineeringIcon from '@mui/icons-material/Engineering';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import StoreIcon from '@mui/icons-material/Store';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import SaporeDiPaneLogo from '../../media/sapore-di-pane-logo.jpeg'
import {styles} from "./AppLayoutStyles";
import {
    AUTHORIZATIONS_PATH,
    BACKOFFICE_USERS_PATH,
    BUDGET_PATH,
    CREATE_ORDER_PATH,
    CURRENT_ACCOUNTS_PATH,
    CUSTOMER_ONBOARDINGS_PATH,
    CUSTOMERS_PATH,
    DASHBOARD_PATH,
    LOGIN_PATH,
    ORDERS_PATH,
    PURCHASE_ORDERS_PATH,
    SUPPORT_PATH,
    USERS_PATH,
    ALLOWANCES_PATH,
    LOGISTICA_PATH,
    SOLD_BOXES_BY_CUSTOMER_AND_ARTICLE_PATH,
    MARKETPLACE_DEMO_PATH,
    CREATE_LEAD_PATH,
    LEAD_ASSIGNMENT_QUEUE_PATH,
    LEAD_PIPELINE_PATH,
} from "../../routes";
import {useApplication} from "../../ApplicationProvider";
import openLogistica from "../logistica/openLogistica";
import openMarketplaceDemo from "../demo_tienda/openMarketplaceDemo";
import commitPendingFieldEdits from "../../forms/commitPendingFieldEdits";
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GridViewIcon from '@mui/icons-material/GridView';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ThemeToggleButton from '../../components/buttons/ThemeToggleButton';
import ChatWidgetScreen from "../chat_widget/ChatWidgetScreen";
import NewsScreen from "../news/NewsScreen";

function AppLayoutScreen() {
    const theme = useMuiTheme();
    const navigator = useNavigate();
    const currentLocation = useLocation()
    const application = useApplication()
    const [drawerOpen, setDrawerOpen] = useState(true);

    useEffect(() => {
        const handler = () => commitPendingFieldEdits();
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    const style = styles(theme, drawerOpen);
    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    const onNewSectionSelection = (section) => {
        commitPendingFieldEdits();
        navigator(section);
    }

    const drawerMenuItem = (title, IconComponent, sectionPath) => {
        const isItemSelected = currentLocation.pathname === sectionPath

        const button = (
            <ListItemButton onClick={() => onNewSectionSelection(sectionPath)} selected={isItemSelected}
                            sx={[
                                {px: 2, py: 0.75},
                                drawerOpen ? {justifyContent: 'initial'} : {justifyContent: 'center'},
                            ]}>
                <ListItemIcon
                    sx={[
                        {minWidth: 0, justifyContent: 'center'},
                        drawerOpen ? {mr: 2.5} : {mr: 'auto'},
                    ]}>
                    <IconComponent fontSize="small"/>
                </ListItemIcon>
                {drawerOpen && <ListItemText primary={title} primaryTypographyProps={{fontSize: '0.8125rem'}}/>}
            </ListItemButton>
        )

        return (
            <ListItem key={title} disablePadding>
                {drawerOpen ? button : <Tooltip title={title} placement="right">{button}</Tooltip>}
            </ListItem>
        )
    }

    // Ítem que dispara una acción externa (no navegación interna), como abrir
    // SDP-Logística vía handoff.
    const externalDrawerMenuItem = (title, IconComponent, onClick) => {
        const button = (
            <ListItemButton onClick={onClick}
                            sx={[
                                {px: 2, py: 0.75},
                                drawerOpen ? {justifyContent: 'initial'} : {justifyContent: 'center'},
                            ]}>
                <ListItemIcon
                    sx={[
                        {minWidth: 0, justifyContent: 'center'},
                        drawerOpen ? {mr: 2.5} : {mr: 'auto'},
                    ]}>
                    <IconComponent fontSize="small"/>
                </ListItemIcon>
                {drawerOpen && <ListItemText primary={title} primaryTypographyProps={{fontSize: '0.8125rem'}}/>}
            </ListItemButton>
        )

        return (
            <ListItem key={title} disablePadding>
                {drawerOpen ? button : <Tooltip title={title} placement="right">{button}</Tooltip>}
            </ListItem>
        )
    }

    return (
        <Box sx={style.appLayoutContainer}>
            <AppBar sx={style.appBar} position="fixed">
                <Toolbar sx={style.toolbar}>
                    <Box sx={style.leftSection}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={toggleDrawer}
                            edge="start"
                        >
                            {drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                        </IconButton>
                    </Box>
                    <Box sx={style.centerSection}>
                        <Box sx={style.brandContainer}>
                            <Box
                                component="img"
                                alt="Sapore Di Pane Logo"
                                src={SaporeDiPaneLogo}
                                sx={style.brandLogo}
                            />
                            <Typography variant="h6" sx={style.brandName}>Sapore Di Pane</Typography>
                        </Box>
                    </Box>
                    <Box sx={style.rightSection}>
                        <ThemeToggleButton />
                        {/*<IconButton*/}
                        {/*    size="large"*/}
                        {/*    aria-label="account of current user"*/}
                        {/*    aria-controls="menu-appbar"*/}
                        {/*    aria-haspopup="true"*/}
                        {/*    color="inherit"*/}
                        {/*>*/}
                        {/*    <AccountCircle/>*/}
                        {/*</IconButton>*/}
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            color="inherit"
                        >
                            <LogoutIcon onClick={async () => {
                                commitPendingFieldEdits();
                                const sessionIsActive = await application.session().logOut()
                                if (!sessionIsActive) {
                                    navigator(LOGIN_PATH)
                                }
                            }}/>
                        </IconButton>
                    </Box>

                </Toolbar>
            </AppBar>

            <Box sx={style.appDrawerAndContentContainer}>
                <Drawer variant="permanent" sx={style.appLayoutDrawer}>
                    <Box sx={style.drawerContentContainer}>
                        <List sx={{py: 0}}>
                            {visibleMenuGroups().map((group, groupIndex) => (
                                <React.Fragment key={group.label}>
                                    {groupIndex > 0 && <Divider sx={style.groupDivider}/>}
                                    {drawerOpen && (
                                        <Typography component="div" sx={style.sectionHeader}>{group.label}</Typography>
                                    )}
                                    {group.items.map((item) => {
                                        const opensExternalTarget = Boolean(item.onExternalOpen);
                                        return opensExternalTarget
                                            ? externalDrawerMenuItem(item.title, item.icon, () => item.onExternalOpen(application))
                                            : drawerMenuItem(item.title, item.icon, item.path);
                                    })}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                </Drawer>

                <Box sx={style.appLayoutContent}>
                    <Outlet/>
                </Box>
            </Box>
            <ChatWidgetScreen />
            <NewsScreen/>
        </Box>);


    function visibleMenuGroups() {
        return MENU_GROUPS
            .map((group) => ({
                ...group,
                items: group.items.filter((item) => application.canAccessPath(item.path)),
            }))
            .filter((group) => group.items.length > 0);
    }
}

const MENU_GROUPS = [
    {
        label: 'Operación',
        items: [
            {title: 'Pedidos', icon: ShoppingCartIcon, path: ORDERS_PATH},
            {title: 'Crear Pedido', icon: AddShoppingCartIcon, path: CREATE_ORDER_PATH},
            {title: 'Órdenes de Compra', icon: FactCheckIcon, path: PURCHASE_ORDERS_PATH},
            {title: 'Autorizaciones', icon: VpnKeyIcon, path: AUTHORIZATIONS_PATH},
            {title: 'Cajas por Artículo', icon: GridViewIcon, path: SOLD_BOXES_BY_CUSTOMER_AND_ARTICLE_PATH},
        ],
    },
    {
        label: 'Clientes',
        items: [
            {title: 'Clientes', icon: StoreIcon, path: CUSTOMERS_PATH},
            {title: 'Alta de clientes', icon: AddBusinessIcon, path: CUSTOMER_ONBOARDINGS_PATH},
            {title: 'Cuentas Corrientes', icon: AccountBalanceIcon, path: CURRENT_ACCOUNTS_PATH},
        ],
    },
    {
        label: 'Comercial',
        items: [
            {title: 'Dashboard Comercial', icon: DashboardIcon, path: DASHBOARD_PATH},
            {title: 'Presupuestos', icon: RequestQuoteIcon, path: BUDGET_PATH},
            {title: 'Bonificaciones', icon: LoyaltyIcon, path: ALLOWANCES_PATH},
        ],
    },
    {
        label: 'CRM',
        items: [
            {title: 'Nuevo lead', icon: PersonAddIcon, path: CREATE_LEAD_PATH},
            {title: 'Asignación de leads', icon: AssignmentIndIcon, path: LEAD_ASSIGNMENT_QUEUE_PATH},
            {title: 'Pipeline', icon: ViewKanbanIcon, path: LEAD_PIPELINE_PATH},
        ],
    },
    {
        label: 'Administración',
        items: [
            {title: 'Usuarios Marketplace', icon: PeopleAltIcon, path: USERS_PATH},
            {title: 'Usuarios Backoffice', icon: EngineeringIcon, path: BACKOFFICE_USERS_PATH},
            {title: 'Soporte', icon: BuildIcon, path: SUPPORT_PATH},
        ],
    },
    {
        label: 'Logística',
        items: [
            {title: 'Sistema de Logística', icon: LocalShippingIcon, path: LOGISTICA_PATH, onExternalOpen: openLogistica},
        ],
    },
    {
        label: 'Demo',
        items: [
            {title: 'Demo Tienda', icon: StorefrontIcon, path: MARKETPLACE_DEMO_PATH, onExternalOpen: openMarketplaceDemo},
        ],
    },
];

export default observer(AppLayoutScreen);
